import Dexie, { type Table } from 'dexie';
import type { Profile, LevelProgress, Achievement, Session, GameType, Difficulty } from '../types';
import { generateUUID } from '../utils/uuid';

export class GameDatabase extends Dexie {
  profiles!: Table<Profile>;
  progress!: Table<LevelProgress>;
  achievements!: Table<Achievement>;
  sessions!: Table<Session>;

  constructor() {
    super('SpaceMathQuest');

    this.version(1).stores({
      profiles: '++id, name, createdAt',
      progress: '++id, profileId, level, [profileId+level], completedAt',
      achievements: '++id, profileId, achievementId, [profileId+achievementId], unlockedAt',
      sessions: '++id, profileId, level, startedAt'
    });

    this.version(2).stores({
      profiles: '++id, name, createdAt',
      progress: '++id, profileId, level, gameType, [profileId+gameType+level], completedAt',
      achievements: '++id, profileId, achievementId, [profileId+achievementId], unlockedAt',
      sessions: '++id, profileId, level, startedAt'
    }).upgrade(trans => {
      return trans.table('progress').toCollection().modify((record: Record<string, unknown>) => {
        if (!record.gameType)   record.gameType   = 'multiplication';
        if (!record.difficulty) record.difficulty = 'medium';
      });
    });
  }
}

export const db = new GameDatabase();

export async function getProfileWithStats(profileId: string) {
  const profile = await db.profiles.get(profileId);
  if (!profile) return null;
  
  const progress = await db.progress.where('profileId').equals(profileId).toArray();
  
  return {
    ...profile,
    totalStars: progress.reduce((sum, p) => sum + p.stars, 0),
    totalScore: progress.reduce((sum, p) => sum + p.score, 0),
    levelsCompleted: progress.length,
    bestStreak: progress.length > 0 ? Math.max(...progress.map(p => p.bestStreak)) : 0
  };
}

export async function saveProgress(
  profileId: string,
  level: number,
  gameType: GameType,
  difficulty: Difficulty,
  stars: 1 | 2 | 3,
  score: number,
  accuracy: number,
  time: number,
  bestStreak: number
) {
  const existingProgress = await db.progress
    .where('[profileId+gameType+level]')
    .equals([profileId, gameType, level])
    .first();

  if (existingProgress) {
    if (score > existingProgress.score) {
      await db.progress.update(existingProgress.id!, {
        stars: Math.max(stars, existingProgress.stars) as 1 | 2 | 3,
        difficulty,
        score,
        accuracy,
        time,
        bestStreak: Math.max(bestStreak, existingProgress.bestStreak),
        completedAt: new Date()
      });
    }
  } else {
    await db.progress.add({
      id: generateUUID(),
      profileId,
      level,
      gameType,
      difficulty,
      stars,
      score,
      accuracy,
      time,
      bestStreak,
      completedAt: new Date()
    });
  }
}

export async function deleteProfile(profileId: string) {
  await db.transaction('rw', db.profiles, db.progress, db.achievements, db.sessions, async () => {
    await db.profiles.delete(profileId);
    await db.progress.where('profileId').equals(profileId).delete();
    await db.achievements.where('profileId').equals(profileId).delete();
    await db.sessions.where('profileId').equals(profileId).delete();
  });
}